// ═══════════════════════════════════════════════════════════════
// Intake — Roster (Individual List)
// Searchable, filterable, sortable table with detail modal
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Avatar,
  Table, SegmentedControl, Stack, Select, Center, ThemeIcon,
  Modal, Textarea, SimpleGrid, Divider,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconSearch, IconUserPlus, IconAlertTriangle,
  IconSortAscending, IconSortDescending, IconPhone, IconNotes,
  IconMedicalCross, IconPill, IconUser, IconPrinter, IconEdit, IconDeviceFloppy,
} from '@tabler/icons-react';
import { useIntakeStore } from '../../store/intakeStore';
import { useAuthStore } from '../../store/authStore';
import type { Individual } from '../../store/intakeStore';
import { formatDateTime, calcAge, TRIAGE_COLORS } from './lib/helpers';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

type SortKey = 'lastName' | 'dob' | 'triage' | 'tagCode' | 'status' | 'intakeMethod' | 'datecreated';

const TRIAGE_OPTIONS = [
  { value: 'green', label: 'Green', color: 'green' },
  { value: 'yellow', label: 'Yellow', color: 'yellow' },
  { value: 'red', label: 'Red', color: 'red' },
] as const;

// ── Code 128B barcode encoder ──────────────────────────────────
const CODE128B_START = 104;
const CODE128_STOP  = 106;
const CODE128_PATTERNS = [
  '11011001100','11001101100','11001100110','10010011000','10010001100',
  '10001001100','10011001000','10011000100','10001100100','11001001000',
  '11001000100','11000100100','10110011100','10011011100','10011001110',
  '10111001100','10011101100','10011100110','11001110010','11001011100',
  '11001001110','11011100100','11001110100','11100101100','11100100110',
  '11101100100','11100110100','11100110010','11011011000','11011000110',
  '11000110110','10100011000','10001011000','10001000110','10110001000',
  '10001101000','10001100010','11010001000','11000101000','11000100010',
  '10110111000','10110001110','10001101110','10111011000','10111000110',
  '10001110110','11101110110','11010001110','11000101110','11011101000',
  '11011100010','11011101110','11101011000','11101000110','11100010110',
  '11101101000','11101100010','11100011010','11101111010','11001000010',
  '11110001010','10100110000','10100001100','10010110000','10010000110',
  '10000101100','10000100110','10110010000','10110000100','10011010000',
  '10011000010','10000110100','10000110010','11000010010','11001010000',
  '11110111010','11000010100','10001111010','10100111100','10010111100',
  '10010011110','10111100100','10011110100','10011110010','11110100100',
  '11110010100','11110010010','11011011110','11011110110','11110110110',
  '10101111000','10100011110','10001011110','10111101000','10111100010',
  '11110101000','11110100010','10111011110','10111101110','11101011110',
  '11110101110','11010000100','11010010000','11010011100','1100011101011',
];

function encodeCode128B(text: string) {
  const codes = [CODE128B_START];
  for (let i = 0; i < text.length; i++) codes.push(text.charCodeAt(i) - 32);
  let checksum = codes[0];
  for (let i = 1; i < codes.length; i++) checksum += codes[i] * i;
  codes.push(checksum % 103);
  codes.push(CODE128_STOP);
  return codes.map(c => CODE128_PATTERNS[c]).join('');
}

function drawBarcode(pdf: jsPDF, text: string, x: number, y: number, width: number, height: number) {
  const pattern = encodeCode128B(text);
  const barWidth = width / pattern.length;
  pdf.setFillColor(17, 45, 78);
  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === '1') pdf.rect(x + i * barWidth, y, barWidth, height, 'F');
  }
}

/** Print a single ID-style badge for one individual */
export async function printBadge(person: Individual, incidentName = '') {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [101.6, 152.4] }); // 4x6 inch
  const w = 101.6, h = 152.4;
  const navy: [number, number, number] = [17, 45, 78];
  const triageColors: Record<string, [number, number, number]> = {
    red: [220, 38, 38], yellow: [217, 119, 6], green: [22, 163, 74],
  };
  const tc = triageColors[(person.triage || 'green').toLowerCase()] || navy;

  // Background
  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, w, h, 'F');

  // Top color banner
  pdf.setFillColor(...navy);
  pdf.rect(0, 0, w, 22, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(255, 255, 255);
  pdf.text('OpenGov EMC', w / 2, 9, { align: 'center' });
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text(incidentName || 'Emergency Management', w / 2, 16, { align: 'center' });

  // Photo or avatar placeholder
  const photoY = 28;
  if (person.photoUrl && person.photoUrl.startsWith('data:')) {
    try { pdf.addImage(person.photoUrl, 'JPEG', (w - 32) / 2, photoY, 32, 32); } catch { /* skip */ }
  } else {
    pdf.setFillColor(226, 232, 240);
    pdf.roundedRect((w - 32) / 2, photoY, 32, 32, 3, 3, 'F');
    pdf.setFontSize(20);
    pdf.setTextColor(148, 163, 184);
    pdf.text(((person.firstName?.[0] || '') + (person.lastName?.[0] || '')).toUpperCase(), w / 2, photoY + 20, { align: 'center' });
  }

  // Name
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(...navy);
  const fullName = `${person.firstName || ''} ${person.lastName || ''}`.trim();
  pdf.text(fullName, w / 2, 70, { align: 'center' });

  // DOB / Age / Gender line
  const age = calcAge(person.dob);
  const infoLine = [
    person.dob ? `DOB: ${person.dob}` : '',
    age != null ? `Age: ${age}` : '',
    person.gender || '',
  ].filter(Boolean).join('  ·  ');
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(100, 116, 139);
  if (infoLine) pdf.text(infoLine, w / 2, 77, { align: 'center' });

  // Triage banner
  pdf.setFillColor(...tc);
  pdf.roundedRect(20, 82, w - 40, 10, 2, 2, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.text(`TRIAGE: ${(person.triage || 'GREEN').toUpperCase()}`, w / 2, 89, { align: 'center' });

  // Tag code
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(...navy);
  pdf.text(person.tagCode || '', w / 2, 103, { align: 'center' });

  // Barcode
  drawBarcode(pdf, person.tagCode || '', 16, 107, w - 32, 10);

  // QR code
  try {
    const qrDataUrl = await QRCode.toDataURL(person.tagCode || 'N/A', {
      width: 300, margin: 1, errorCorrectionLevel: 'M',
      color: { dark: '#112D4E', light: '#ffffff' },
    });
    pdf.addImage(qrDataUrl, 'PNG', (w - 26) / 2, 120, 26, 26);
  } catch { /* skip */ }

  // Bottom text
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  pdf.setTextColor(148, 163, 184);
  pdf.text(`Checked in: ${person.checkedInAt ? new Date(person.checkedInAt).toLocaleString() : 'N/A'}`, w / 2, h - 4, { align: 'center' });

  pdf.save(`badge_${person.tagCode || 'unknown'}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

/** Print Avery 5163 property labels with QR code + Code 128B barcode + triage color */
export async function printPropertyLabels(people: Individual[], incidentName = '') {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const cols = 2, rows = 5;
  const labelsPerPage = cols * rows;
  const labelW = 101.6, labelH = 50.8;
  const marginX = (pageW - cols * labelW) / 2;
  const marginY = (pageH - rows * labelH) / 2;
  const navy: [number, number, number] = [17, 45, 78];
  const triageColors: Record<string, [number, number, number]> = {
    red: [220, 38, 38], yellow: [217, 119, 6], green: [22, 163, 74],
  };

  // Pre-generate QR codes for unique individuals
  const qrCache: Record<string, string> = {};
  const uniqueTags = [...new Set(people.map(p => p.tagCode).filter(Boolean))];
  for (const tag of uniqueTags) {
    try {
      qrCache[tag] = await QRCode.toDataURL(tag, {
        width: 200, margin: 1, errorCorrectionLevel: 'M',
        color: { dark: '#112D4E', light: '#ffffff' },
      });
    } catch { /* skip if QR gen fails */ }
  }

  // Single person → fill page with copies
  const labelData = people.length === 1
    ? Array(labelsPerPage).fill(people[0])
    : people;

  labelData.forEach((p: Individual, idx: number) => {
    const posOnPage = idx % labelsPerPage;
    const col = posOnPage % cols;
    const row = Math.floor(posOnPage / cols);
    if (idx > 0 && posOnPage === 0) pdf.addPage();

    const x = marginX + col * labelW;
    const y = marginY + row * labelH;

    // Cut guide
    pdf.setDrawColor(210, 210, 210);
    pdf.setLineWidth(0.2);
    pdf.rect(x, y, labelW, labelH);

    // Triage color strip
    const tc = triageColors[(p.triage || 'green').toLowerCase()] || navy;
    pdf.setFillColor(...tc);
    pdf.rect(x, y, 4, labelH, 'F');

    // QR code (right side)
    const qrDataUrl = qrCache[p.tagCode];
    if (qrDataUrl) {
      pdf.addImage(qrDataUrl, 'PNG', x + labelW - 28, y + 3, 24, 24);
    }

    // Tag code (large, top left)
    pdf.setFont('courier', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(...navy);
    pdf.text(p.tagCode || '', x + 10, y + 10);

    // Code 128B barcode (below tag code)
    drawBarcode(pdf, p.tagCode || '', x + 10, y + 13, 50, 8);

    // Name
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...navy);
    pdf.text(`${p.firstName || ''} ${p.lastName || ''}`.trim(), x + 10, y + 28);

    // DOB + Age + Gender
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    const age = calcAge(p.dob);
    const dobLine = [
      p.dob ? `DOB: ${p.dob}` : '',
      age != null ? `Age: ${age}` : '',
      p.gender || '',
    ].filter(Boolean).join('  ·  ');
    if (dobLine) pdf.text(dobLine, x + 10, y + 34);

    // Triage badge
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...tc);
    pdf.text(`TRIAGE: ${(p.triage || 'green').toUpperCase()}`, x + 10, y + 41);

    // Incident name (bottom left)
    if (incidentName) {
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(148, 163, 184);
      pdf.text(incidentName, x + 10, y + labelH - 4);
    }

    // Branding (bottom right)
    pdf.setFontSize(6.5);
    pdf.setTextColor(148, 163, 184);
    pdf.text('OpenGov EMC', x + labelW - 6, y + labelH - 4, { align: 'right' });
  });

  pdf.save(`badges_${new Date().toISOString().slice(0, 10)}.pdf`);
}

export default function Roster() {
  const { individuals, loading, loadIndividuals, updateIndividual, addTriageEntry } = useIntakeStore();
  const { activeIncident, user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [triageFilter, setTriageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('datecreated');
  const [sortDesc, setSortDesc] = useState(true);

  // Detail modal state
  const [selected, setSelected] = useState<Individual | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newTriage, setNewTriage] = useState('');
  const [triageReason, setTriageReason] = useState('');
  const [triageLoading, setTriageLoading] = useState(false);

  // Edit mode state
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Individual>>({});
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    if (activeIncident) {
      loadIndividuals(String(activeIncident.id));
    }
  }, [activeIncident, loadIndividuals]);

  // Handle ?highlight=id from dashboard clicks
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && individuals.length > 0) {
      const ind = individuals.find(i => String(i.id) === highlightId);
      if (ind) {
        openDetail(ind);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, individuals, setSearchParams]);

  const incidentIndividuals = useMemo(() => {
    if (!activeIncident) return [];
    return individuals.filter(i => String(i.incidentId) === String(activeIncident.id));
  }, [individuals, activeIncident]);

  const filtered = useMemo(() => {
    let list = incidentIndividuals;

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        (i.firstName || '').toLowerCase().includes(q) ||
        (i.lastName || '').toLowerCase().includes(q) ||
        (i.tagCode || '').toLowerCase().includes(q) ||
        (i.dob || '').includes(q)
      );
    }

    if (triageFilter !== 'all') {
      list = list.filter(i => (i.triage || 'green') === triageFilter);
    }
    if (statusFilter !== 'all') {
      list = list.filter(i => (i.status || 'active') === statusFilter);
    }

    // Sort
    list = [...list].sort((a, b) => {
      const aVal = (a[sortKey] ?? '').toString().toLowerCase();
      const bVal = (b[sortKey] ?? '').toString().toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortDesc ? -cmp : cmp;
    });

    return list;
  }, [incidentIndividuals, search, triageFilter, statusFilter, sortKey, sortDesc]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDesc(!sortDesc);
    else { setSortKey(key); setSortDesc(false); }
  };

  const openDetail = (ind: Individual) => {
    setSelected(ind);
    setNewTriage(ind.triage || 'green');
    setTriageReason('');
    setEditing(false);
    setEditData({});
    setModalOpen(true);
  };

  const closeDetail = () => {
    setModalOpen(false);
    setSelected(null);
    setNewTriage('');
    setTriageReason('');
    setEditing(false);
    setEditData({});
  };

  const startEditing = () => {
    if (!selected) return;
    setEditData({
      firstName: selected.firstName,
      lastName: selected.lastName,
      dob: selected.dob,
      gender: selected.gender,
      medicalNeeds: selected.medicalNeeds,
      medications: selected.medications,
      emergencyContact: selected.emergencyContact,
      contactPhone: selected.contactPhone,
      notes: selected.notes,
    });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!selected || !activeIncident) return;
    setSaveLoading(true);
    try {
      await updateIndividual(selected.id, editData);
      setSelected({ ...selected, ...editData });
      setEditing(false);
      notifications.show({ title: 'Updated', message: 'Individual record has been updated.', color: 'green' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save changes.',
        color: 'red',
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePrintBadge = async (ind: Individual) => {
    await printBadge(ind, activeIncident?.name || '');
    notifications.show({ title: 'Badge PDF', message: 'Badge PDF generated.', color: 'blue' });
  };

  const handleTriageChange = async () => {
    if (!selected || !activeIncident || !newTriage || newTriage === selected.triage) return;
    setTriageLoading(true);
    try {
      await updateIndividual(selected.id, { triage: newTriage });
      await addTriageEntry({
        individualId: String(selected.id),
        incidentId: String(activeIncident.id),
        triageLevel: newTriage,
        changedBy: user?.firstName
          ? `${user.firstName} ${user.lastName || ''}`.trim()
          : 'Unknown',
        reason: triageReason,
        timestamp: new Date().toISOString(),
      });
      // Update selected in place for the modal
      setSelected({ ...selected, triage: newTriage });
      setTriageReason('');
    } catch (err) {
      console.error('Triage change failed:', err);
    } finally {
      setTriageLoading(false);
    }
  };

  const SortIcon = sortDesc ? IconSortDescending : IconSortAscending;

  // ── No incident selected ──────────────────────────────────
  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="gray">
            <IconAlertTriangle size={30} />
          </ThemeIcon>
          <Title order={4}>No Incident Selected</Title>
          <Text size="sm" c="dimmed">
            Use the incident selector in the top header bar.
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────── */}
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={3} c="black">Roster</Title>
          <Badge size="sm" variant="light" color="blue">
            {filtered.length} of {incidentIndividuals.length}
          </Badge>
        </Group>
        <Button
          leftSection={<IconUserPlus size={14} />}
          size="sm"
          component="a"
          href="/intake/register"
        >
          New Registration
        </Button>
      </Group>

      {/* ── Filters ────────────────────────────────────────── */}
      <Paper p="md" radius="sm" withBorder mb="md">
        <Group gap="md" wrap="wrap">
          <TextInput
            placeholder="Search name, tag, DOB..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          <SegmentedControl
            value={triageFilter}
            onChange={setTriageFilter}
            data={[
              { value: 'all', label: 'All Triage' },
              { value: 'green', label: 'Green' },
              { value: 'yellow', label: 'Yellow' },
              { value: 'red', label: 'Red' },
            ]}
            size="xs"
          />
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v || 'all')}
            data={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'discharged', label: 'Discharged' },
            ]}
            size="xs"
            w={140}
          />
        </Group>
      </Paper>

      {/* ── Table ──────────────────────────────────────────── */}
      <Paper radius="sm" withBorder style={{ overflow: 'auto' }}>
        <Table highlightOnHover striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('lastName')}>
                <Group gap={4}>Name {sortKey === 'lastName' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('dob')}>
                <Group gap={4}>DOB {sortKey === 'dob' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('triage')}>
                <Group gap={4}>Triage {sortKey === 'triage' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('tagCode')}>
                <Group gap={4}>Tag Code {sortKey === 'tagCode' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>
                <Group gap={4}>Status {sortKey === 'status' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('intakeMethod')}>
                <Group gap={4}>Method {sortKey === 'intakeMethod' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('datecreated')}>
                <Group gap={4}>Registered {sortKey === 'datecreated' && <SortIcon size={12} />}</Group>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text ta="center" c="dimmed" py="lg">
                    {loading ? 'Loading...' : 'No individuals found.'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              filtered.map((ind) => {
                const age = calcAge(ind.dob);
                return (
                  <Table.Tr
                    key={ind.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openDetail(ind)}
                  >
                    <Table.Td>
                      <Text size="sm" fw={500}>{ind.lastName}, {ind.firstName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{ind.dob || '\u2014'}</Text>
                      {age !== null && <Text size="xs" c="dimmed">Age {age}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        color={ind.triage === 'red' ? 'red' : ind.triage === 'yellow' ? 'yellow' : 'green'}
                        variant="filled"
                      >
                        {(ind.triage || 'green').toUpperCase()}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="outline" color="gray" ff="monospace">
                        {ind.tagCode}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        variant="light"
                        color={ind.status === 'discharged' ? 'gray' : 'blue'}
                      >
                        {ind.status || 'active'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" tt="capitalize">{ind.intakeMethod || '\u2014'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{formatDateTime(ind.datecreated)}</Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* ── Detail Modal ───────────────────────────────────── */}
      <Modal
        opened={modalOpen}
        onClose={closeDetail}
        title={
          selected ? (
            <Group gap="sm">
              <Text fw={600}>{selected.lastName}, {selected.firstName}</Text>
              <Badge
                color={selected.triage === 'red' ? 'red' : selected.triage === 'yellow' ? 'yellow' : 'green'}
                variant="filled"
                size="sm"
              >
                {(selected.triage || 'green').toUpperCase()}
              </Badge>
            </Group>
          ) : 'Individual Detail'
        }
        size="lg"
      >
        {selected && (
          <Stack gap="md">
            {/* Photo + basic info */}
            <Group align="flex-start" gap="md">
              <Avatar
                size={80}
                radius="md"
                src={selected.photoUrl || null}
                color={selected.triage === 'red' ? 'red' : selected.triage === 'yellow' ? 'yellow' : 'green'}
              >
                {selected.firstName?.[0]}{selected.lastName?.[0]}
              </Avatar>
              <Stack gap={4} style={{ flex: 1 }}>
                <Text fw={600} size="lg">
                  {selected.firstName} {selected.lastName}
                </Text>
                <Group gap="sm">
                  <Badge
                    color={selected.triage === 'red' ? 'red' : selected.triage === 'yellow' ? 'yellow' : 'green'}
                    variant="filled"
                    size="sm"
                  >
                    {(selected.triage || 'green').toUpperCase()}
                  </Badge>
                  <Badge
                    variant="light"
                    color={selected.status === 'discharged' ? 'gray' : 'blue'}
                    size="sm"
                  >
                    {selected.status || 'active'}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed">Tag: {selected.tagCode}</Text>
              </Stack>
            </Group>

            {/* Action buttons: Edit & Print Badge */}
            <Group gap="xs">
              {!editing && (
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<IconEdit size={14} />}
                  onClick={startEditing}
                >
                  Edit Info
                </Button>
              )}
              <Button
                variant="light"
                size="xs"
                leftSection={<IconPrinter size={14} />}
                onClick={() => handlePrintBadge(selected)}
              >
                Print Badge
              </Button>
              <Button
                variant="subtle"
                size="xs"
                color="gray"
                leftSection={<IconPrinter size={14} />}
                onClick={async () => {
                  await printPropertyLabels([selected], activeIncident?.name || '');
                  notifications.show({ title: 'Labels PDF', message: 'Property labels PDF generated.', color: 'blue' });
                }}
              >
                Print Labels
              </Button>
            </Group>

            <Divider />

            {/* Detail fields — view or edit mode */}
            {editing ? (
              <Stack gap="sm">
                <SimpleGrid cols={2} spacing="sm">
                  <TextInput
                    label="First Name"
                    value={editData.firstName || ''}
                    onChange={e => setEditData({ ...editData, firstName: e.currentTarget.value })}
                  />
                  <TextInput
                    label="Last Name"
                    value={editData.lastName || ''}
                    onChange={e => setEditData({ ...editData, lastName: e.currentTarget.value })}
                  />
                  <TextInput
                    label="Date of Birth"
                    type="date"
                    value={editData.dob || ''}
                    onChange={e => setEditData({ ...editData, dob: e.currentTarget.value })}
                  />
                  <Select
                    label="Gender"
                    value={editData.gender || ''}
                    onChange={v => setEditData({ ...editData, gender: v || '' })}
                    data={[
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' },
                      { value: 'other', label: 'Other' },
                      { value: 'unknown', label: 'Prefer not to say' },
                    ]}
                    clearable
                  />
                </SimpleGrid>
                <Textarea
                  label="Medical Needs"
                  value={editData.medicalNeeds || ''}
                  onChange={e => setEditData({ ...editData, medicalNeeds: e.currentTarget.value })}
                  minRows={2}
                />
                <Textarea
                  label="Medications"
                  value={editData.medications || ''}
                  onChange={e => setEditData({ ...editData, medications: e.currentTarget.value })}
                  minRows={2}
                />
                <SimpleGrid cols={2} spacing="sm">
                  <TextInput
                    label="Emergency Contact"
                    value={editData.emergencyContact || ''}
                    onChange={e => setEditData({ ...editData, emergencyContact: e.currentTarget.value })}
                  />
                  <TextInput
                    label="Contact Phone"
                    value={editData.contactPhone || ''}
                    onChange={e => setEditData({ ...editData, contactPhone: e.currentTarget.value })}
                  />
                </SimpleGrid>
                <Textarea
                  label="Notes"
                  value={editData.notes || ''}
                  onChange={e => setEditData({ ...editData, notes: e.currentTarget.value })}
                  minRows={2}
                />
                <Group justify="flex-end">
                  <Button variant="subtle" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button
                    size="sm"
                    leftSection={<IconDeviceFloppy size={14} />}
                    onClick={handleSaveEdit}
                    loading={saveLoading}
                  >
                    Save Changes
                  </Button>
                </Group>
              </Stack>
            ) : (
              <>
                <SimpleGrid cols={2} spacing="sm">
                  <DetailField icon={<IconUser size={14} />} label="Date of Birth" value={
                    selected.dob
                      ? `${selected.dob}${calcAge(selected.dob) !== null ? ` (Age ${calcAge(selected.dob)})` : ''}`
                      : '\u2014'
                  } />
                  <DetailField icon={<IconUser size={14} />} label="Gender" value={selected.gender || '\u2014'} />
                  <DetailField icon={<IconMedicalCross size={14} />} label="Medical Needs" value={selected.medicalNeeds || '\u2014'} />
                  <DetailField icon={<IconPill size={14} />} label="Medications" value={selected.medications || '\u2014'} />
                  <DetailField icon={<IconPhone size={14} />} label="Emergency Contact" value={
                    selected.emergencyContact
                      ? `${selected.emergencyContact}${selected.contactPhone ? ` (${selected.contactPhone})` : ''}`
                      : '\u2014'
                  } />
                  <DetailField icon={<IconNotes size={14} />} label="Notes" value={selected.notes || '\u2014'} />
                </SimpleGrid>

                <Text size="xs" c="dimmed">
                  Registered: {formatDateTime(selected.datecreated)} | Method: {selected.intakeMethod || '\u2014'}
                </Text>
              </>
            )}

            {/* ── Change Triage (only for active individuals) ── */}
            {selected.status !== 'discharged' && !editing && (
              <>
                <Divider label="Change Triage Level" labelPosition="left" />

                <Group gap="sm">
                  {TRIAGE_OPTIONS.map((opt) => (
                    <Button
                      key={opt.value}
                      variant={newTriage === opt.value ? 'filled' : 'outline'}
                      color={opt.color}
                      size="xs"
                      onClick={() => setNewTriage(opt.value)}
                      style={
                        newTriage === opt.value
                          ? undefined
                          : { borderColor: TRIAGE_COLORS[opt.value], color: TRIAGE_COLORS[opt.value] }
                      }
                    >
                      {opt.label}
                    </Button>
                  ))}
                </Group>

                <Textarea
                  label="Reason for triage change"
                  placeholder="Brief reason for reclassification..."
                  value={triageReason}
                  onChange={(e) => setTriageReason(e.currentTarget.value)}
                  minRows={2}
                  disabled={newTriage === (selected.triage || 'green')}
                />

                <Button
                  onClick={handleTriageChange}
                  loading={triageLoading}
                  disabled={!newTriage || newTriage === (selected.triage || 'green') || !triageReason.trim()}
                  color={newTriage === 'red' ? 'red' : newTriage === 'yellow' ? 'yellow' : 'green'}
                >
                  Update Triage to {(newTriage || '').toUpperCase()}
                </Button>
              </>
            )}
          </Stack>
        )}
      </Modal>
    </div>
  );
}

// ── Small helper component for detail fields ──────────────────
function DetailField({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div>
      <Group gap={4} mb={2}>
        {icon}
        <Text size="xs" fw={600} c="dimmed">{label}</Text>
      </Group>
      <Text size="sm">{value}</Text>
    </div>
  );
}
